// URL <-> state sync, ported from legacy stbarts (index.html L4413-4600).
// Same query-string scheme so a URL works on both legacy-cmp.* and alpha-cmp.*:
//   /?target=front/en/pages/home&mode=last&res=01-desktop-1920&ver=current&zoomMode=auto
//   /?view=kanban
//
// Mode aliases for legacy compatibility:
//   mode=new   -> 'last'  (legacy single-NEW view)
//   mode=old   -> 'last'  (alpha has no old-only; degrade gracefully)
//
// Zoom: legacy stores a float scale (e.g. "1.20" for 120%); alpha stores an
// integer percent. We convert on read/write.

import { createEffect, on } from 'solid-js';
import { actions, stores } from './store';
import type { ViewMode, ZoomFitMode } from './store';

type ParsedUrl = {
  target: string | null;
  mode: ViewMode;
  res: string | null;
  ver: string;
  zoomPercent: number | null;
  zoomMode: ZoomFitMode;
  kanban: boolean;
};

function parseViewMode(v: string | null): ViewMode {
  switch (v) {
    case 'compare':
    case 'slider':
    case 'overlay':
    case 'diff':
    case 'history':
    case 'last':
    case 'kanban':
      return v;
    case 'new':
    case 'old':
      return 'last';
    default:
      return 'last';
  }
}

function parseZoomMode(v: string | null): ZoomFitMode {
  switch (v) {
    case 'fitWidth':
    case 'fitHeight':
    case 'auto':
      return v;
    case 'manual':
      return null;
    default:
      return null;
  }
}

function parseFromLocation(): ParsedUrl {
  const params = new URLSearchParams(window.location.search);
  const zoomRaw = params.get('zoom');
  const zoomPercent = zoomRaw ? Math.round(parseFloat(zoomRaw) * 100) : null;
  const kanban = params.get('view') === 'kanban';
  return {
    target: params.get('target'),
    mode: kanban ? 'kanban' : parseViewMode(params.get('mode')),
    res: params.get('res'),
    // legacy URL writes ver=current|old|<timestamp>. We pass it through and
    // apply via setLeftVersion below; the store auto-promotes 'current' to
    // 'old' when an OLD capture is present, so deep-link to legacy URLs
    // without an explicit ver param keeps working.
    ver: params.get('ver') || 'current',
    zoomPercent: Number.isFinite(zoomPercent as number) ? zoomPercent : null,
    zoomMode: parseZoomMode(params.get('zoomMode')),
    kanban,
  };
}

// Build the canonical query string for the current store state.
function buildQuery(): string {
  const s = stores.state;
  if (s.viewMode === 'kanban') {
    return '?view=kanban';
  }
  const target = s.targets.find((t) => t.id === s.activeTargetId)?.path;
  if (!target) return '';
  const params = new URLSearchParams({
    target,
    mode: s.viewMode,
    res: s.activeResolution ?? '',
    ver: s.leftVersion || 'current',
    zoomMode: s.zoomFitMode ?? 'manual',
  });
  if (s.zoomFitMode === null) {
    params.set('zoom', (s.zoomPercent / 100).toFixed(2));
  }
  return '?' + params.toString();
}

let suppressSync = false;

async function applyParsed(parsed: ParsedUrl): Promise<void> {
  suppressSync = true;
  try {
    if (parsed.kanban) {
      actions.setViewMode('kanban');
      return;
    }

    if (parsed.mode !== stores.state.viewMode) {
      actions.setViewMode(parsed.mode);
    }

    if (parsed.target) {
      const t = stores.state.targets.find((x) => x.path === parsed.target);
      if (t && t.id !== stores.state.activeTargetId) {
        await actions.selectTarget(t.id);
      }
    }

    if (parsed.res && parsed.res !== stores.state.activeResolution) {
      actions.setActiveResolution(parsed.res);
    }

    if (parsed.ver && parsed.ver !== stores.state.leftVersion) {
      actions.setLeftVersion(parsed.ver);
    }

    if (parsed.zoomMode !== null) {
      actions.setZoomFitMode(parsed.zoomMode);
    } else if (parsed.zoomPercent !== null) {
      actions.setZoom(parsed.zoomPercent);
    }
  } finally {
    // Release in a microtask so the chained setState calls above settle
    // before reactive effects can fire URL writes.
    queueMicrotask(() => {
      suppressSync = false;
    });
  }
}

// Restore on boot (called once after projects+targets are loaded).
export async function restoreFromUrl(): Promise<void> {
  await applyParsed(parseFromLocation());
}

// Subscribe to state changes -> write URL. target changes use pushState
// (creates a history entry); finer-grained changes use replaceState.
export function installUrlWriter(): void {
  let lastTarget = stores.state.activeTargetId;
  let lastKanban = stores.state.viewMode === 'kanban';

  createEffect(
    on(
      () => [
        stores.state.activeTargetId,
        stores.state.viewMode,
        stores.state.activeResolution,
        stores.state.leftVersion,
        stores.state.zoomFitMode,
        stores.state.zoomPercent,
      ],
      () => {
        if (suppressSync) return;
        const q = buildQuery();
        if (!q) return;
        const url = '/' + q;
        if (window.location.search === q) return;

        const isKanban = stores.state.viewMode === 'kanban';
        const targetChanged = stores.state.activeTargetId !== lastTarget;
        const kanbanToggled = isKanban !== lastKanban;
        lastTarget = stores.state.activeTargetId;
        lastKanban = isKanban;

        if (targetChanged || kanbanToggled) {
          window.history.pushState({ parsed: parseFromLocation() }, '', url);
        } else {
          window.history.replaceState({ parsed: parseFromLocation() }, '', url);
        }
      },
    ),
  );

  window.addEventListener('popstate', () => {
    void applyParsed(parseFromLocation());
  });
}
